/**
 * @fileoverview Componente para mostrar descripción de producto con expansión
 * @module ProductDescription
 * @category Component - Atom
 * @since 1.0.0
 * @version 1.0.0
 */
import React, {
  useEffect, useRef, useState, memo
} from 'react';
import { useTranslation } from 'react-i18next';

/**
 * @typedef {Object} Product
 * @description Objeto producto con descripción
 * @property {string} description - Descripción HTML del producto
 */

/**
 * Componente Atom para mostrar descripción expandible de producto.
 *
 * Implementa el patrón **Atom** de Atomic Design para mostrar
 * descripciones de productos con funcionalidad de expandir/contraer.
 *
 * @component ProductDescription
 * @category Component - Atom
 *
 * @description
 * **Características:**
 * - Descripción expandible con límite de altura
 * - Detección automática de overflow
 * - Botón "Ver más/Ver menos" dinámico
 * - Renderización segura de HTML
 * - Estilos Tailwind CSS migrados
 * - Optimizado con React.memo
 *
 * @param {Object} props - Propiedades del componente
 * @param {Product} props.product - Producto con descripción
 *
 * @returns {JSX.Element} Elemento JSX con descripción expandible
 *
 * @example
 * ```jsx
 * <ProductDescription product={productData} />
 * ```
 */
const ProductDescription = memo(({ product }) => {
  const [expanded, setExpanded] = useState(false);
  const [isOverflowing, setIsOverflowing] = useState(false);
  const contentRef = useRef(null);

  const { t } = useTranslation('Inbox');

  useEffect(() => {
    const el = contentRef.current;
    if (el && el.scrollHeight > 100) {
      setIsOverflowing(true);
    }
  }, [product.description]);

  return (
    <div className="tw-py-2.5 tw-text-xs">
      <div
        ref={contentRef}
        className={`
          tw-mb-0 
          ${expanded
          ? 'tw-max-h-none'
          : 'tw-max-h-20 tw-overflow-hidden tw-relative'
          }
        `}
        dangerouslySetInnerHTML={{ __html: product.description }}
      />
      {isOverflowing && (
        <span
          className="tw-font-medium tw-text-sm tw-text-gray-600 tw-underline tw-cursor-pointer"
          onClick={() => setExpanded(!expanded)}
        >
          {expanded ? t('products.readLess', 'Ver menos') : t('products.readMore', 'Ver más')}
        </span>
      )}
    </div>
  );
});

/**
 * Nombre de display para debugging y herramientas de desarrollo.
 *
 * @type {string}
 */
ProductDescription.displayName = 'ProductDescription';

export default ProductDescription;
